import React, {useEffect, useState} from 'react'
import {bookingService} from '../../../services/booking/bookingService'
import LoadingIndicator from '../../../shared/UI/LoadingIndicator'
import {useBookingData} from './useBookingData'
import BookingNotification from './BookingNotificationModal'
import {hasClientId} from '../../../authentication/clientIdService'
import {dashboardService} from '../../../services/dashboardService'
import {EmployeeDetailsResponse} from '../../../types/employee'
import {BookingBookingPayload, BookingCheckInOutPost} from '../../../services/booking/types'
import oktaService from '../../../services/oktaService'

function CheckAvailability() {
  const [hasBooking, setHasBooking] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetailsResponse>()
  const currentQueryString = window.location.search
  const bookingData = useBookingData()
  const [showBookingNotificationModal, setShowBookingNotificationModal] = useState(false)
  const [showNoBookingNotificationModal, setShowNoBookingNotificationModal] = useState(false)
  const [textDeskIsNotAvailable, setTextDeskIsNotAvailable] = useState('')
  const [bookingText, setBookingText] = useState('')
  const [employeeDataFetched, setEmployeeDataFetched] = useState<boolean>(false)
  const [bookingDataFetched, setBookingDataFetched] = useState<boolean>(false)
  const [bookings, setBookings] = useState<BookingBookingPayload[]>([])
  const [allBooking, setAllBooking] = useState<BookingBookingPayload[]>([])

  // Add state to track client ID status
  const [clientIdStatus, setClientIdStatus] = useState<boolean>(hasClientId())

  // Helper functions to manage modal state
  const showBookingModal = (text: string) => {
    setShowNoBookingNotificationModal(false) // Close the other modal first
    setBookingText(text)
    setShowBookingNotificationModal(true)
  }

  const showNoBookingModal = (text: string) => {
    setShowBookingNotificationModal(false) // Close the other modal first
    setTextDeskIsNotAvailable(text)
    setShowNoBookingNotificationModal(true)
  }

  // Add useEffect to monitor client ID changes
  useEffect(() => {
    const checkClientId = () => {
      const currentStatus = hasClientId()
      if (currentStatus !== clientIdStatus) {
        setClientIdStatus(currentStatus)
      }
    }

    // Check immediately
    checkClientId()

    // Set up interval to check periodically (adjust interval as needed)
    const interval = setInterval(checkClientId, 1000)

    // Also listen for storage events if hasClientId depends on localStorage
    const handleStorageChange = () => {
      checkClientId()
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [clientIdStatus])

  useEffect(() => {
    if (clientIdStatus) {
      dashboardService
        .getEmployeeDetails()
        .then(response => {
          setEmployeeDetails(response)
        })
        .finally(() => {
          setEmployeeDataFetched(true)
        })
    } else {
      setEmployeeDataFetched(true)
    }
  }, [clientIdStatus]) // Use clientIdStatus instead of hasClientId()

  useEffect(() => {
    if (!bookingData) return

    const controller = new AbortController()

    setIsLoading(true)
    bookingService
      .search(controller, {
        date: bookingData.fromDate,
        from: bookingData.fromTime,
        to: bookingData.toTime,
        floorplanId: bookingData.floorPlanId
      })
      .then(response => {
        const bookingsFound = response.bookings.filter(
          b => Number(b.featureId) === Number(bookingData.featureId) && b.statusId === 2
        )

        const bookingFound = bookingsFound.length > 0
        setAllBooking(response.bookings.filter(b => b.statusId === 2))

        setBookings(bookingsFound)

        setHasBooking(bookingFound)
      })
      .catch(_ => {
        setError('Failed to check availability. Please try again.')
      })
      .finally(() => {
        setBookingDataFetched(true)
        setIsLoading(false)
      })

    return () => controller.abort()
  }, [bookingData, clientIdStatus]) // Use clientIdStatus instead of hasClientId()

  useEffect(() => {
    if (!bookingDataFetched) return

    if (!employeeDataFetched) return

    const employeeId = Number(employeeDetails?.employeeId)

    if (employeeId && allBooking.length > 0) {
      const bookingForSameEmployee = allBooking.filter(v => v.employeeId === employeeId)

      const bookingForSameEmployeeWithSameDesk = bookingForSameEmployee.filter(
        v => v.featureId === Number(bookingData?.featureId)
      )

      if (bookingForSameEmployeeWithSameDesk.length > 0) {
        const body: Partial<BookingCheckInOutPost> = {
          bookingID: bookingForSameEmployeeWithSameDesk[0]?.id,
          userId: employeeDetails?.employeeId ?? 0,
          type: 'in',
          actionDate: new Date().toISOString(),
          deskName: bookingData?.deskDetails?.deskName,
          anyIssues: ''
        }

        bookingService.checkInOut(body).then(_ => {
          showNoBookingModal('You have successfully checked into this desk.')
        })

        return
      }

      if (bookingForSameEmployee.length > 0) {
        const [{deskName}] = bookingForSameEmployee
        showNoBookingModal(`You already have a booking today for desk ${deskName}`)
        return
      }
    }

    if (!hasBooking) {
      showBookingModal('Desk is available.')
      return
    }

    if (!clientIdStatus) {
      showBookingModal('This desk has been reserved.')
    } else if (clientIdStatus) {
      showNoBookingModal('Desk is booked please scan another code or book via planner365.')
    }

  }, [
    employeeDetails,
    bookings,
    hasBooking,
    bookingData?.deskDetails?.deskName,
    bookingDataFetched,
    employeeDataFetched,
    allBooking,
    bookingData?.featureId])

  const resetNotificationModal = () => {
    setShowBookingNotificationModal(false)
  }

  const resetNoNotificationModal = () => {
    setShowNoBookingNotificationModal(false)
  }

  // Add this useEffect for page visibility handling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Reset modal states when page becomes visible again
        setShowBookingNotificationModal(false)
        setShowNoBookingNotificationModal(false)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Update your click handlers
  const handleBookClick = () => {
    // Clear modal states before redirect
    setShowBookingNotificationModal(false)
    setShowNoBookingNotificationModal(false)
    
    let pathUrl = ''
    if (!clientIdStatus && !hasBooking) {
      pathUrl = `/booking/qr-code-booking${currentQueryString}`
    } else if (!clientIdStatus && hasBooking) {
      pathUrl = `/booking/qr-code${currentQueryString}`
    }

    if (pathUrl) {
      oktaService.triggerOktaRedirect(pathUrl)
    } else {
      window.location.replace(`/booking/qr-code-booking${currentQueryString}`)
    }
  }

  const handleRedirectClick = () => {
    // Clear modal states before redirect
    setShowBookingNotificationModal(false)
    setShowNoBookingNotificationModal(false)
    
    window.location.replace(`/booking`)
  }

  if (isLoading) {
    return <LoadingIndicator show={isLoading} />
  }

  if (error) {
    return <div className="error-message">{error}</div>
  }

  let buttonText = 'Sign in'
  if (clientIdStatus) {
    buttonText = 'Book & check in'
  }

  return (
    <div className="availability-container">
      {showBookingNotificationModal && (
        <div className="available-desk">
          <BookingNotification
            open={showBookingNotificationModal}
            titleText={bookingText}
            buttonLabel={buttonText}
            onClick={handleBookClick}
            message=""
            type="question"
            isDisabled={false}
            onClose={resetNotificationModal}
          />
        </div>
      )}

      {showNoBookingNotificationModal && (
        <div className="unavailable-desk">
          <BookingNotification
            open={showNoBookingNotificationModal}
            titleText={textDeskIsNotAvailable}
            buttonLabel="Go to planner365"
            onClick={handleRedirectClick}
            message=""
            type="clash"
            isDisabled={false}
            onClose={resetNoNotificationModal}
          />
        </div>
      )}
    </div>
  )
}

export default CheckAvailability
